/**
 * Bank Accounts section embedded inside the client /profile page.
 *
 * Shows the client's active client_bank_accounts in a list, with an
 * inline "Deactivate" affordance and a soft Add CTA via the shared
 * `BankAccountSelector` component.
 *
 * No edit affordance by design — changes are "deactivate + register
 * new" so the audit trail stays clean (an unalterable record of every bank
 * account change, which carries material implications for AML and wire reconciliation).
 */
import { useCallback, useEffect, useState } from 'react';
import bankAccountsService, {
  type ClientBankAccount,
} from '../../services/bankAccounts.service';
import BankAccountSelector from './BankAccountSelector';
import StepUpCodeField from '../security/StepUpCodeField';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { describeMoneyRefusal, type MoneyRefusal } from '../../lib/moneyRefusal';
import RefusalNotice from './RefusalNotice';
import { colors } from '../../styles/theme';
import { readerCountryName } from '../../lib/countries';


interface Props {
  /** The owning client's user id — pass the authenticated user's id. */
  clientId: string;
}

export default function ProfileBankAccountsSection({
  clientId,
}: Readonly<Props>): JSX.Element {
  const [accounts, setAccounts] = useState<ClientBankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  // Row currently awaiting a step-up code before deactivation, + the code.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [removeCode, setRemoveCode] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await bankAccountsService.listForClient(clientId);
      setAccounts(rows);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load bank accounts.'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { reload(); }, [reload]);

  const openRemove = useCallback((id: string) => {
    setError(null);
    setRefusal(null);
    setRemoveCode('');
    setPendingId(id);
  }, []);

  const cancelRemove = useCallback(() => {
    setPendingId(null);
    setRemoveCode('');
  }, []);

  const confirmRemove = useCallback(async (a: ClientBankAccount) => {
    const label = a.nickname || a.holder_name;
    setActionId(a.id);
    setError(null);
    setRefusal(null);
    try {
      await bankAccountsService.deactivate(clientId, a.id, removeCode.trim());
      setAccounts((prev) => prev.filter((x) => x.id !== a.id));
      setPendingId(null);
      setRemoveCode('');
      setFlash(`"${label}" deactivated.`);
      globalThis.setTimeout(() => setFlash(null), 5000);
    } catch (e: unknown) {
      // An account still inside its lock is a protection, not a failed action.
      const designed = describeMoneyRefusal(e);
      if (designed !== null) {
        setRefusal(designed);
        setPendingId(null);
        setRemoveCode('');
      } else if (getApiErrorCode(e) === 'mfa_not_enrolled') {
        setError('Turn on two-factor authentication in your security settings first.');
      } else {
        setError(getApiErrorMessage(e, 'Could not deactivate.'));
      }
    } finally {
      setActionId(null);
    }
  }, [clientId, removeCode]);

  return (
    <section className="mt-6">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3"
          style={{ color: colors.inkSoft }}>
        Bank accounts
      </h2>

      <div className="rounded-2xl p-5"
           style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>

        {flash && (
          <div className="rounded-lg p-3 mb-3 text-sm"
               style={{ background: colors.successBg, color: colors.success,
                        border: `1px solid ${colors.successBorder}` }}>
            {flash}
          </div>
        )}

        {refusal && <RefusalNotice refusal={refusal} />}

        {error && (
          <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger,
                        border: `1px solid ${colors.dangerBorder}` }}>
            {error}
          </div>
        )}

        {loading && (
          <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
        )}

        {!loading && !error && accounts.length === 0 && (
          <p className="text-sm mb-3" style={{ color: colors.muted }}>
            Add the bank account in your name that you&apos;ll send contributions from. Money we
            send back to you goes to that same account.
          </p>
        )}

        {!loading && accounts.length > 0 && (
          <ul className="space-y-2 mb-4">
            {accounts.map((a) => (
              <li key={a.id}
                  className="p-3 rounded-lg"
                  style={{ border: `1px solid ${colors.border}` }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium" style={{ color: colors.ink }}>
                      {a.nickname || a.holder_name}
                    </div>
                    <div className="text-xs font-mono" style={{ color: colors.muted }}>
                      {a.account_type} · {readerCountryName(a.country_code)} · {a.currency_code} · ····{a.account_number_last4}
                    </div>
                    {a.nickname && (
                      <div className="text-xs mt-0.5" style={{ color: colors.mutedSoft }}>
                        {a.holder_name}
                      </div>
                    )}
                  </div>
                  {pendingId !== a.id && (
                    <button
                      type="button"
                      onClick={() => { openRemove(a.id); }}
                      className="text-xs px-2 py-1 rounded-md"
                      style={{ background: 'transparent', color: colors.danger,
                               border: `1px solid ${colors.dangerBorder}` }}
                    >
                      Deactivate
                    </button>
                  )}
                </div>

                {pendingId === a.id && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.border}` }}>
                    <p className="text-xs mb-2" style={{ color: colors.muted }}>
                      Removing a payout account requires two-factor authentication.
                      Enter the 6-digit code from your authenticator app. Past
                      transfers are not affected; you can re-add this account later.
                    </p>
                    <StepUpCodeField
                      id={`remove-code-${a.id}`}
                      value={removeCode}
                      onChange={setRemoveCode}
                      hint="From your authenticator app."
                    />
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => { confirmRemove(a); }}
                        disabled={actionId === a.id || removeCode.trim().length < 6}
                        className="text-xs px-3 py-2 rounded-md font-semibold"
                        style={{ background: colors.danger, color: '#fff' }}
                      >
                        {actionId === a.id ? 'Deactivating…' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelRemove}
                        disabled={actionId === a.id}
                        className="text-xs px-3 py-2 rounded-md"
                        style={{ background: 'transparent', color: colors.inkSoft,
                                 border: `1px solid ${colors.border}` }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {!loading && (
          <BankAccountSelector
            clientId={clientId}
            hideSelection
            onSelectionChange={() => { reload(); }}
          />
        )}
      </div>
    </section>
  );
}
